import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useRef } from 'react';
import type { ViewProps } from 'react-native';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';

type FadingViewProps = ViewProps & {
    visible: boolean;
    duration?: number;
    children: React.ReactNode;
};

export function FadingView({ children, visible, duration = 300, style, ...rest }: FadingViewProps) {
    const opacity = useRef(new Animated.Value(visible ? 1 : 0)).current;

    useEffect(() => {
        Animated.timing(opacity, {
            toValue: visible ? 1 : 0,
            duration,
            useNativeDriver: true,
            easing: Easing.inOut(Easing.quad),
        }).start();
    }, [visible, duration, opacity]);

    return (
        <Animated.View style={[style, { opacity }]} {...rest}>
            {children}
        </Animated.View>
    );
}

type ScalingViewProps = ViewProps & {
    scale?: number;
    duration?: number;
};

export function ScalingView({ children, scale = 1, duration = 150, style, ...rest }: ScalingViewProps) {
    const scaleValue = useRef(new Animated.Value(scale ? 1 : 0)).current;

    useEffect(() => {
        Animated.spring(scaleValue, {
            toValue: scale,
            useNativeDriver: true,
            bounciness: 10,
            speed: 40,
        }).start();
    }, [scale]);

    return (
        <Animated.View style={[style, { transform: [{ scale: scaleValue }] }]} {...rest}>
            {children}
        </Animated.View>
    );
}

export function Shimmer({ children, animationOutputRange }: { children: React.ReactNode; animationOutputRange?: [number, number] }) {
    const config = getShimmerConfig();
    const shimmerAnimation = useRef(new Animated.Value(config.initialValue)).current;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.timing(shimmerAnimation, {
                toValue: 1,
                duration: config.duration,
                useNativeDriver: config.useNativeDriver,
            }),
        );
        animation.start();
        return () => animation.stop();
    }, [shimmerAnimation, config]);

    const translateX = shimmerAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: animationOutputRange || [-600, 600],
    });

    return (
        <View style={{ overflow: 'hidden' }}>
            {children}
            <Animated.View style={[StyleSheet.absoluteFill, { transform: [{ translateX }] }]}>
                <LinearGradient
                    colors={['transparent', 'rgba(255, 255, 255, 0.8)', 'transparent']}
                    end={{ x: 1, y: 0.5 }}
                    start={{ x: 0, y: 0.5 }}
                    style={StyleSheet.absoluteFill}
                />
            </Animated.View>
        </View>
    );
}

function getShimmerConfig() {
    const shimmerEffect = {
        duration: 2000,
        initialValue: 0,
        inputRange: [0, 1],
    };
    const config = Platform.select({
        web: {
            useNativeDriver: false,
            ...shimmerEffect,
        },
        default: {
            useNativeDriver: true,
            ...shimmerEffect,
        },
    });
    return config;
}
